import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject, signal } from '@angular/core';
import { ToastService } from 'angular-toastify';
import {
  AdminActivityLog,
  adminActivityFieldLabel,
  adminActivityValueLabel,
} from '../../../../../core/models/system-settings/admin-activity-log.model';
import { MapperAdminActivityLogService } from '../../../../../core/mappers/system-settings/mapper-admin-activity-log.service';
import { ApiAdminActivityLogService } from '../../../../../core/services/api/system-settings/api-admin-activity-log.service';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';

@Component({
  selector: 'app-modal-admin-activity-history',
  standalone: true,
  imports: [CommonModule, PssLoadingComponent],
  templateUrl: './modal-admin-activity-history.component.html',
  styleUrl: './modal-admin-activity-history.component.css',
})
export class ModalAdminActivityHistoryComponent implements OnChanges {
  /** e.g. parking_card, city_parking_vehicle */
  @Input() entityType = '';
  @Input() entityId = 0;
  @Input() title = 'ประวัติการเปลี่ยนแปลง';
  @Input() subtitle = '';
  @Output() close = new EventEmitter<void>();

  private readonly api = inject(ApiAdminActivityLogService);
  private readonly mapper = inject(MapperAdminActivityLogService);
  private readonly toast = inject(ToastService);

  items = signal<AdminActivityLog[]>([]);
  isLoading = signal(false);
  expandedId = signal<number | null>(null);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entityType'] || changes['entityId']) {
      this.load();
    }
  }

  closeModal(): void {
    this.close.emit();
  }

  toggleExpand(id: number): void {
    this.expandedId.set(this.expandedId() === id ? null : id);
  }

  actionLabel(action: string): string {
    switch (action) {
      case 'create':
        return 'สร้าง';
      case 'update':
        return 'แก้ไข';
      case 'delete':
        return 'ลบ';
      default:
        return action || '—';
    }
  }

  /** Body field path → Thai form label. */
  fieldLabel(field: string): string {
    return adminActivityFieldLabel(field);
  }

  valueLabel(value: string | null | undefined): string {
    return adminActivityValueLabel(value);
  }

  trackById(_: number, item: AdminActivityLog): number {
    return item.id;
  }

  private load(): void {
    if (!this.entityType?.trim() || this.entityId <= 0) {
      this.items.set([]);
      return;
    }

    this.isLoading.set(true);
    this.api
      .getAdminActivityLogsForTable({
        page: 1,
        pageSize: 100,
        entityType: this.entityType.trim(),
        entityId: this.entityId,
      })
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapTable(res);
          // oldest → newest for timeline
          const ordered = [...mapped.data].sort((a: AdminActivityLog, b: AdminActivityLog) => {
            const ta = a.createDate ? new Date(a.createDate).getTime() : 0;
            const tb = b.createDate ? new Date(b.createDate).getTime() : 0;
            return ta - tb || a.id - b.id;
          });
          this.items.set(ordered);
          this.isLoading.set(false);
        },
        error: () => {
          this.items.set([]);
          this.isLoading.set(false);
          this.toast.error('โหลดประวัติล้มเหลว');
        },
      });
  }
}
